import { useRef, useState } from 'react'
import { Upload } from 'lucide-react'
import { Button, Drawer, Input, Radio, toast } from '@/components/ui'
import { useUploadQuestionnaire } from '../api/queries'
import type { QuestionnaireMode, QuestionnaireRow } from '../api/types'

const ACCEPT =
  '.pdf,.docx,.xlsx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'

export interface UploadDrawerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Import (answered questionnaires → library) is the regulatory team's. */
  canImport: boolean
  onUploaded: (row: QuestionnaireRow) => void
}

export function UploadDrawer({ open, onOpenChange, canImport, onUploaded }: UploadDrawerProps) {
  const [mode, setMode] = useState<QuestionnaireMode>('new')
  const [file, setFile] = useState<File | null>(null)
  const [title, setTitle] = useState('')
  const [requester, setRequester] = useState('')
  const [dueOn, setDueOn] = useState('')
  const fileInput = useRef<HTMLInputElement>(null)
  const upload = useUploadQuestionnaire()

  function reset() {
    setMode('new')
    setFile(null)
    setTitle('')
    setRequester('')
    setDueOn('')
  }

  function submit() {
    if (!file) return
    upload.mutate(
      { file, mode, title, requester_name: requester, due_on: dueOn || undefined },
      {
        onSuccess: (row) => {
          toast.success(
            mode === 'new'
              ? 'Uploaded — reading and answering it now'
              : 'Uploaded — reading its answers now',
          )
          reset()
          onOpenChange(false)
          onUploaded(row)
        },
        onError: (err) =>
          toast.danger(`Upload failed${err instanceof Error ? `: ${err.message}` : ''}`),
      },
    )
  }

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      surface="form"
      title="Upload questionnaire"
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button variant="primary" disabled={!file} loading={upload.isPending} onClick={submit}>
            <Upload size={14} /> Upload
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <fieldset className="flex flex-col gap-2">
          <legend className="mb-1 text-sm font-medium">What is it?</legend>
          <Radio
            name="questionnaire-mode"
            checked={mode === 'new'}
            onChange={() => setMode('new')}
            label="A questionnaire to answer — fill it in from the answer library"
          />
          {canImport ? (
            <Radio
              name="questionnaire-mode"
              checked={mode === 'import'}
              onChange={() => setMode('import')}
              label="One SPI already answered — add its answers to the library"
            />
          ) : null}
        </fieldset>

        <div className="flex flex-col gap-1 text-sm">
          <span>File (PDF, Word or Excel)</span>
          <div className="flex items-center gap-2">
            <Button variant="secondary" onClick={() => fileInput.current?.click()}>
              Choose file
            </Button>
            <span className="truncate text-secondary">{file ? file.name : 'No file chosen'}</span>
          </div>
          <input
            ref={fileInput}
            type="file"
            accept={ACCEPT}
            className="sr-only"
            aria-label="Questionnaire file"
            onChange={(e) => {
              const f = e.target.files?.[0] ?? null
              setFile(f)
              if (f && !title)
                setTitle(
                  f.name
                    .replace(/\.(pdf|docx|xlsx)$/i, '')
                    .replace(/_+/g, ' ')
                    .trim(),
                )
              e.target.value = ''
            }}
          />
        </div>

        <label className="flex flex-col gap-1 text-sm">
          Title
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. Acme — Vendor Audit 2026"
          />
        </label>
        <label className="flex flex-col gap-1 text-sm">
          Customer
          <Input
            value={requester}
            onChange={(e) => setRequester(e.target.value)}
            placeholder="Read from the form if left blank"
          />
        </label>
        {mode === 'new' ? (
          <label className="flex flex-col gap-1 text-sm">
            Due
            <Input type="date" value={dueOn} onChange={(e) => setDueOn(e.target.value)} />
          </label>
        ) : null}
      </div>
    </Drawer>
  )
}
